import { useEffect, useRef, useState } from 'react'

/**
 * Turns a table wrapper into a horizontal scroller ONLY while it needs to be.
 *
 * Constitution directive 14 says the document is the app's only scrollbar. That
 * is easy to state and slightly awkward to implement, because CSS refuses to
 * scroll one axis while leaving the other visible: set `overflow-x: auto` and
 * `overflow-y` computes to `auto` alongside it. A wrapper left permanently on
 * `overflow: auto` is therefore permanently a VERTICAL scroll container as well,
 * which is the thing the directive forbids — and, less obviously, it is also the
 * reason a sticky table header stops working, since sticky resolves against the
 * nearest scrollport and that scrollport would be the wrapper rather than the
 * page.
 *
 * So the wrapper stays `overflow: visible` until the table genuinely does not
 * fit across, and only then gets `data-xscroll`. The payoff is that on every
 * table that does fit — nearly all of them, since `hideBelow` drops columns as
 * the viewport narrows — the header pins to the viewport under the topbar, and
 * no table ever grows a vertical scrollbar.
 *
 * Measured with a ResizeObserver on both the wrapper and the table, because
 * either can change without the other: the viewport narrowing resizes the
 * wrapper, and a column being revealed or a row loading widens the table.
 */
export function useXScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [overflows, setOverflows] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const measure = () => {
      // Measure the CHILD against the wrapper, never the wrapper against itself.
      // `scrollWidth` only reports overflow for elements that have a scrollable
      // overflow region, and `overflow: visible` — our resting state — has none:
      // the table spills out of the box in full view and the wrapper reports
      // `scrollWidth === clientWidth`, so a self-measurement can never once
      // become true and the attribute would never turn on. It is a measurement
      // that reads as correct and is unconditionally false.
      const child = node.firstElementChild
      if (!child) return
      // A one-pixel tolerance: sub-pixel layout rounding otherwise flickers the
      // attribute on and off at exactly the width where they are equal, and a
      // scrollbar that appears and vanishes as you resize reads as a paint bug.
      setOverflows(child.getBoundingClientRect().width > node.clientWidth + 1)
    }

    measure()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    // The table, not just the wrapper — the wrapper's own box does not change
    // when a column appears inside it.
    const table = node.firstElementChild
    if (table) observer.observe(table)
    return () => observer.disconnect()
  })

  return { ref, xScroll: overflows || undefined }
}
